import * as React from 'react';
import { connect } from 'react-redux';
import { isPassed, isFailed, isInProgress } from 'utils/comparison';
import { withTheme } from 'styled-components';
import { Tooltip } from 'components/common';
import { localize } from 'core/localization';
import { transparentize } from 'utils/color';
import CircularProgressbar from 'react-circular-progressbar';
import * as settingsSelectors from 'store/settings/selectors';
import {
  getAffectProgressQuestionsCountInSection,
  getCorrectlyAffectProgressQuestionsCountInSection,
  getAffectCorrectlyAnsweredQuestionsCount,
  getAnsweredSectionQuestionsCount,
  getSectionProgress
} from 'store/sections/selectors';
import {
  getCourseNonInformationQuestionCount,
  getCourseInformationQuestionCount,
  getAffectProgressQuestionsCount,
  getQuestionCountToReachMasteryScore,
  getQuestionPercentageToReachMasteryScore,
  getCorrectlyAffectProgressAnsweredQuestionsCount,
  getAttemptedQuestionsCount
} from 'store/questions/selectors';
import * as courseSelectors from 'store/course/selectors';
import { RootAppState } from 'store/types';
import { KEYS, CIRCULAR_PROGRESSBAR } from 'constants/common';
import {
  PopoverContentWrapper,
  PopoverContent,
  ClosePopoverIcon,
  QuestionsProgress,
  CongratsMessageWrapper,
  CongratsMessageTitle,
  NotPassedMessageWrapper,
  FailedMessageWrapper,
  FailedMessageTitle,
  PopoverButton,
  QuestionProgressText
} from './ProgressPopover.styled';

type ProgressPopoverProps = {
  score: number;
  status: number;
  affectProgressQuestionsCountInSection?: number;
  colors?: { [key: string]: any };
  popoverPosition: any;
  children?: any;
  correctlyAffectProgressQuestionsCountInSection?: number;
  affectCorrectlyAnsweredQuestionsCount?: number;
  answeredSectionQuestionsCount?: number;
  sectionProgress?: number;
  isSubmitAtOnce?: boolean;
  isCourseSubmitted?: boolean;
  sectionId: string;
  questions?: any;
  theme?: any;
  ariaLabelledby?: string;
  isOverallMasteryScore?: boolean;
  isScoringOfContentPagesAllowed?: boolean;
  courseNonInformationQuestionCount?: number;
  courseInformationQuestionCount?: number;
  affectProgressQuestionsCount?: number;
  masteryScoreValue?: number;
  questionCountToReachMasteryScore?: number;
  questionPercentageToReachMasteryScore?: number;
  correctlyAffectProgressAnsweredQuestionsCount?: number;
  isCoursePassed?: boolean;
  isCourseProgress?: boolean;
  isCourseFailed?: boolean;
  courseScore?: number;
  attemptedQuestionsCount?: number;
};

type ProgressPopoverState = {
  isExpanded: boolean;
};

export class ProgressPopover extends React.Component<ProgressPopoverProps, ProgressPopoverState> {
  popoverRef: any;

  constructor(props: ProgressPopoverProps) {
    super(props);
    this.state = {
      isExpanded: false
    };
    this.popoverRef = React.createRef();
  }

  toggleOnClick = (evt: any) => {
    if (!evt.key) {
      this.setState({
        isExpanded: !this.state.isExpanded
      });
    }
  };

  closePopup = () => {
    if (this.state.isExpanded) {
      this.setState({
        isExpanded: false
      });
    }
  };

  handleClosePopupKeyDown = (evt: any) => {
    if ([KEYS.ENTER.toLowerCase(), KEYS.ESCAPE.toLowerCase()].includes(evt.key.toLowerCase())) {
      this.closePopup();
    }
  };

  componentDidUpdate() {
    setTimeout(() => {
      const element = document.getElementById('progress-popover');
      if (element && this.state.isExpanded) {
        element.focus();
      }
      if (this.popoverRef.current && !this.state.isExpanded) {
        this.popoverRef.current.focus();
      }
    }, 0);
  }

  shouldComponentUpdate(nextProps: ProgressPopoverProps, nextState: ProgressPopoverState) {
    return this.state.isExpanded !== nextState.isExpanded;
  }

  render() {
    const { isExpanded } = this.state;
    const { popoverPosition, ariaLabelledby } = this.props;

    return (
      <Tooltip
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        trigger={'click'}
        open={isExpanded}
        onRequestClose={this.closePopup}
        position={popoverPosition}
        tooltipTheme={'light'}
        arrow={true}
        interactive
        html={this.renderPopoverContent()}
      >
        <PopoverButton
          tabIndex={0}
          onClick={this.toggleOnClick}
          ref={this.popoverRef}
          aria-labelledby={ariaLabelledby}
        />
      </Tooltip>
    );
  }

  getCircularProgressbarStroke = () => {
    const { status, colors, isCourseFailed, isOverallMasteryScore } = this.props;
    const failingObject = isOverallMasteryScore ? isCourseFailed : isFailed(status);

    return failingObject && colors ? colors.incorrectColor : colors && colors.mainColor;
  };

  renderSubmitOnceQuestionProgress = () => {
    const {
      status,
      correctlyAffectProgressQuestionsCountInSection,
      affectProgressQuestionsCountInSection,
      attemptedQuestionsCount,
      sectionProgress = 0,
      isCourseSubmitted = false,
      score,
      colors
    } = this.props;
    const templateTheme = this.props.theme;
    const isSectionPassed = isCourseSubmitted && isPassed(status);

    return (
      <React.Fragment>
        <QuestionsProgress isPassed={isSectionPassed} theme={templateTheme}>
          <QuestionProgressText>
            {isCourseSubmitted
              ? correctlyAffectProgressQuestionsCountInSection
              : attemptedQuestionsCount}
            /{affectProgressQuestionsCountInSection}
          </QuestionProgressText>
          <CircularProgressbar
            strokeWidth={2}
            percentage={isCourseSubmitted ? score : sectionProgress}
            styles={{
              path: {
                stroke: this.getCircularProgressbarStroke(),
                strokeLinecap: CIRCULAR_PROGRESSBAR.STROKE_LINECAP,
                transition: CIRCULAR_PROGRESSBAR.TRANSITION
              },
              trail: {
                stroke: colors && transparentize(colors.textColor, 0.02)
              }
            }}
          />
        </QuestionsProgress>
        {this.renderPopoverMessageContent()}
      </React.Fragment>
    );
  };

  renderQuestionProgress = () => {
    const {
      status,
      correctlyAffectProgressQuestionsCountInSection,
      affectProgressQuestionsCountInSection,
      score,
      colors
    } = this.props;
    const templateTheme = this.props.theme;
    const isSectionPassed = isPassed(status);

    return (
      <React.Fragment>
        <QuestionsProgress isPassed={isSectionPassed} theme={templateTheme}>
          <QuestionProgressText>
            {correctlyAffectProgressQuestionsCountInSection}/{affectProgressQuestionsCountInSection}
          </QuestionProgressText>
          <CircularProgressbar
            strokeWidth={2}
            percentage={score}
            styles={{
              path: {
                stroke: this.getCircularProgressbarStroke(),
                strokeLinecap: CIRCULAR_PROGRESSBAR.STROKE_LINECAP,
                transition: CIRCULAR_PROGRESSBAR.TRANSITION
              },
              trail: {
                stroke: colors && transparentize(colors.textColor, 0.02)
              }
            }}
          />
        </QuestionsProgress>
        {this.renderPopoverMessageContent()}
      </React.Fragment>
    );
  };

  renderMasteryProgress = () => {
    const {
      colors,
      affectProgressQuestionsCount,
      questionPercentageToReachMasteryScore,
      correctlyAffectProgressAnsweredQuestionsCount,
      isCoursePassed
    } = this.props;
    const templateTheme = this.props.theme;
    const isMasteryCoursePassed = isCoursePassed !== undefined ? isCoursePassed : false;

    return (
      <React.Fragment>
        <QuestionsProgress isPassed={isMasteryCoursePassed} theme={templateTheme}>
          <QuestionProgressText>
            {correctlyAffectProgressAnsweredQuestionsCount}/{affectProgressQuestionsCount}
          </QuestionProgressText>
          <CircularProgressbar
            strokeWidth={2}
            percentage={
              questionPercentageToReachMasteryScore !== undefined
                ? questionPercentageToReachMasteryScore
                : 0
            }
            styles={{
              path: {
                stroke: this.getCircularProgressbarStroke(),
                strokeLinecap: CIRCULAR_PROGRESSBAR.STROKE_LINECAP,
                transition: CIRCULAR_PROGRESSBAR.TRANSITION
              },
              trail: {
                stroke: colors && transparentize(colors.textColor, 0.02)
              }
            }}
          />
        </QuestionsProgress>
        {this.renderPopoverMasteryScoreMessage()}
      </React.Fragment>
    );
  };

  selectProgressPopup = () => {
    const { isSubmitAtOnce, isOverallMasteryScore } = this.props;

    if (isOverallMasteryScore) {
      return this.renderMasteryProgress();
    }
    return isSubmitAtOnce ? this.renderSubmitOnceQuestionProgress() : this.renderQuestionProgress();
  };

  renderPopoverContent = () => {
    const templateTheme = this.props.theme;

    return (
      <PopoverContentWrapper
        id="progress-popover"
        role="dialog"
        ariaModal={true}
        tabIndex={-1}
        onKeyDown={event => this.handleClosePopupKeyDown(event)}
        theme={templateTheme}
      >
        <PopoverContent>
          <ClosePopoverIcon
            role="button"
            ariaLabel={localize('[close popover]')}
            tabIndex={0}
            onKeyDown={(event: any) => this.handleClosePopupKeyDown(event)}
            onClick={this.toggleOnClick}
            size={14}
            name="close-popup"
            theme={templateTheme}
          />
          {this.selectProgressPopup()}
        </PopoverContent>
      </PopoverContentWrapper>
    );
  };

  renderPopoverMessageContent = () => {
    const {
      status,
      affectProgressQuestionsCountInSection,
      theme,
      affectCorrectlyAnsweredQuestionsCount
    } = this.props;
    return (
      <>
        {isPassed(status) && (
          <CongratsMessageWrapper>
            <CongratsMessageTitle theme={theme} appearance="h4">
              {localize('[passed section title]')}
            </CongratsMessageTitle>
            {localize('[passed section]')}
          </CongratsMessageWrapper>
        )}
        {isFailed(status) && (
          <FailedMessageWrapper>
            <FailedMessageTitle theme={theme} appearance="h4">
              {localize('[failed section title]')}
            </FailedMessageTitle>
            {localize('[failed section x y]', {
              countCorrectlyAnsweredQuestionsInSection: affectCorrectlyAnsweredQuestionsCount,
              countQuestionsInSection: affectProgressQuestionsCountInSection
            })}
          </FailedMessageWrapper>
        )}
        {isInProgress(status) && (
          <NotPassedMessageWrapper>
            {localize('[you need to answer X questions out of Y]', {
              correctQuestionsCount: affectCorrectlyAnsweredQuestionsCount,
              totalQuestionsCount: affectProgressQuestionsCountInSection
            })}
          </NotPassedMessageWrapper>
        )}
      </>
    );
  };

  renderPopoverMasteryScoreMessage = () => {
    const {
      courseNonInformationQuestionCount,
      courseInformationQuestionCount,
      affectProgressQuestionsCount,
      questionCountToReachMasteryScore,
      isCoursePassed,
      isCourseProgress,
      isCourseFailed,
      masteryScoreValue,
      theme
    } = this.props;

    const isCourseOnlyQuestions =
      courseInformationQuestionCount !== undefined &&
      courseNonInformationQuestionCount !== undefined &&
      courseInformationQuestionCount <= 0 &&
      courseNonInformationQuestionCount > 0;
    const isCourseOnlyContents =
      courseInformationQuestionCount !== undefined &&
      courseNonInformationQuestionCount !== undefined &&
      courseInformationQuestionCount > 0 &&
      courseNonInformationQuestionCount <= 0;

    let messageToShow = '[overall answer x y items z]';
    if (isCourseOnlyQuestions) {
      messageToShow = '[overall answer x y questions z]';
    }
    if (isCourseOnlyContents) {
      messageToShow = '[overall answer x y content z]';
    }

    const inProgressMessage = localize(messageToShow, {
      questionCountToReachMasteryScore,
      affectProgressQuestionsCount,
      masteryScoreValue
    });

    return (
      <>
        {isCoursePassed && (
          <CongratsMessageWrapper>
            <CongratsMessageTitle theme={theme} appearance="h4">
              {localize('[passed section title]')}
            </CongratsMessageTitle>
            {localize('[question pool passed]')}
          </CongratsMessageWrapper>
        )}
        {isCourseFailed && (
          <FailedMessageWrapper>
            <FailedMessageTitle theme={theme} appearance="h4">
              {localize('[course is not passed]')}
            </FailedMessageTitle>
            {inProgressMessage}
          </FailedMessageWrapper>
        )}
        {isCourseProgress && <NotPassedMessageWrapper>{inProgressMessage}</NotPassedMessageWrapper>}
      </>
    );
  };
}

function mapStateToProps(state: RootAppState, ownProps: ProgressPopoverProps) {
  return {
    colors: settingsSelectors.getColors(state),
    affectProgressQuestionsCountInSection: getAffectProgressQuestionsCountInSection(
      state,
      ownProps.sectionId
    ),
    correctlyAffectProgressQuestionsCountInSection: getCorrectlyAffectProgressQuestionsCountInSection(
      state,
      ownProps.sectionId
    ),
    affectCorrectlyAnsweredQuestionsCount: getAffectCorrectlyAnsweredQuestionsCount(
      state,
      ownProps.sectionId
    ),
    sectionProgress: getSectionProgress(state, ownProps.sectionId),
    answeredSectionQuestionsCount: getAnsweredSectionQuestionsCount(state, ownProps.sectionId),
    isCourseSubmitted: courseSelectors.isAttemptSubmitted(state),
    isOverallMasteryScore: settingsSelectors.isOverallMasteryScore(state),
    isCoursePassed: courseSelectors.isPassed(state),
    isCourseProgress: courseSelectors.isInProgress(state),
    isCourseFailed: courseSelectors.isFailed(state),
    courseScore: courseSelectors.getScore(state),
    isScoringOfContentPagesAllowed: settingsSelectors.isScoringOfContentPagesAllowed(state),
    courseNonInformationQuestionCount: getCourseNonInformationQuestionCount(state),
    courseInformationQuestionCount: getCourseInformationQuestionCount(state),
    affectProgressQuestionsCount: getAffectProgressQuestionsCount(state),
    masteryScoreValue: settingsSelectors.getMasteryScoreValue(state),
    questionCountToReachMasteryScore: getQuestionCountToReachMasteryScore(state),
    questionPercentageToReachMasteryScore: getQuestionPercentageToReachMasteryScore(state),
    correctlyAffectProgressAnsweredQuestionsCount: getCorrectlyAffectProgressAnsweredQuestionsCount(
      state
    ),
    attemptedQuestionsCount: getAttemptedQuestionsCount(state)
  };
}

export default withTheme(connect(mapStateToProps)(ProgressPopover));
